import { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Shield,
  ChevronDown,
  ChevronRight,
  Ban,
  Trash2,
  Crown,
  Lightbulb,
  Wrench,
} from "lucide-react";
import { api, AICachedInsight, AIInsightSuppression } from "@/lib/api";
import { toast } from "@/lib/toast";
import { useDataStore } from "@/stores/data";
import { useAuthStore } from "@/stores/auth";
import {
  buildUnifiedFeed,
  buildSigningDomainItems,
  PRIORITY_ORDER,
  SOURCE_BADGE,
} from "@/components/action-items/shared";
import { ActionItemCard } from "@/components/action-items/ActionItemCard";

type StatusFilter = "active" | "completed";

// ============================================================================
// Main Component
// ============================================================================

export function Alerts() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    searchParams.get("filter") === "completed" ? "completed" : "active"
  );
  const [showCompleted, setShowCompleted] = useState(false);
  const { activeClientOrg, organization } = useAuthStore();
  const currentTier = organization?.billing_tier || "free";
  const isFreeTier = currentTier === "free";

  // Data sources
  const alerts = useDataStore((state) => state.alerts);
  const alertsLoading = useDataStore((state) => state.alertsLoading);
  const ensureAlerts = useDataStore((state) => state.ensureAlerts);
  const refreshAlerts = useDataStore((state) => state.refreshAlerts);
  const updateAlert = useDataStore((state) => state.updateAlert);
  const remediationTasks = useDataStore((state) => state.remediationTasks);
  const ensureRemediationTasks = useDataStore((state) => state.ensureRemediationTasks);
  const refreshRemediationTasks = useDataStore((state) => state.refreshRemediationTasks);

  const [insights, setInsights] = useState<AICachedInsight[]>([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [suppressions, setSuppressions] = useState<AIInsightSuppression[]>([]);
  const [signingDomainItems, setSigningDomainItems] = useState<import("@/components/action-items/shared").UnifiedItem[]>([]);

  const domains = useDataStore((state) => state.domains);
  const ensureDomains = useDataStore((state) => state.ensureDomains);

  // Deep link: ?id= scrolls to and highlights a specific item
  const deepLinkId = searchParams.get("id");
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const deepLinkHandled = useRef(false);

  useEffect(() => {
    ensureAlerts();
    ensureRemediationTasks();
    ensureDomains();
    loadInsights();
    loadSuppressions();

    const handleClientChange = () => refreshAlerts();
    window.addEventListener("msp-client-changed", handleClientChange);
    return () => window.removeEventListener("msp-client-changed", handleClientChange);
  }, [ensureAlerts, ensureRemediationTasks, ensureDomains, refreshAlerts, activeClientOrg?.id]);

  // Load signing domain misalignment items for all domains
  useEffect(() => {
    if (domains.length === 0 || isFreeTier) return;
    let cancelled = false;

    async function loadSigningDomainItems() {
      const allItems: import("@/components/action-items/shared").UnifiedItem[] = [];
      // Fetch signing domains for each domain (limit to first 5 to avoid excessive requests)
      for (const domain of domains.slice(0, 5)) {
        try {
          const data = await api.getSigningDomains(domain.id);
          const items = buildSigningDomainItems(data.signing_domains, domain.id, domain.domain_name);
          allItems.push(...items);
        } catch {
          // Skip domains that fail
        }
      }
      if (!cancelled) setSigningDomainItems(allItems);
    }

    loadSigningDomainItems();
    return () => { cancelled = true; };
  }, [domains, isFreeTier]);

  async function loadInsights() {
    setInsightsLoading(true);
    try {
      const data = await api.getAIRecommendations();
      setInsights(data.insights || []);
    } catch (error) {
      console.error("Failed to load AI insights:", error);
    } finally {
      setInsightsLoading(false);
    }
  }

  async function loadSuppressions() {
    try {
      const data = await api.getInsightSuppressions();
      setSuppressions(data.suppressions || []);
    } catch (error) {
      console.error("Failed to load suppressions:", error);
    }
  }

  async function handleResolveAlert(alertId: string) {
    try {
      await api.resolveAlert(alertId);
      const alert = alerts.find((a) => a.id === alertId);
      if (alert) {
        updateAlert({ ...alert, status: "resolved", resolved: true, resolved_at: new Date().toISOString() });
      }
    } catch (error) {
      console.error("Failed to resolve alert:", error);
      toast.error("Failed to resolve alert");
    }
  }

  async function handleCompleteTask(taskId: string) {
    try {
      await api.completeTask(taskId);
      await refreshRemediationTasks();
    } catch (error) {
      console.error("Failed to complete task:", error);
      toast.error("Failed to complete task");
    }
  }

  async function handleSkipTask(taskId: string) {
    try {
      await api.skipTask(taskId);
      await refreshRemediationTasks();
    } catch (error) {
      console.error("Failed to skip task:", error);
      toast.error("Failed to skip task");
    }
  }

  async function handleSnoozeInsight(insightId: string) {
    try {
      await api.snoozeInsight(insightId);
      setInsights((prev) => prev.filter((i) => i.id !== insightId));
    } catch (error) {
      console.error("Failed to snooze insight:", error);
      toast.error("Failed to snooze insight");
    }
  }

  async function handleResolveInsight(insightId: string) {
    try {
      await api.resolveInsight(insightId);
      setInsights((prev) => prev.filter((i) => i.id !== insightId));
    } catch (error) {
      console.error("Failed to resolve insight:", error);
      toast.error("Failed to resolve insight");
    }
  }

  async function handleSuppressInsight(insightId: string) {
    try {
      await api.suppressInsight(insightId);
      setInsights((prev) => prev.filter((i) => i.id !== insightId));
      // Reload suppressions to show the new entry
      await loadSuppressions();
    } catch (error) {
      console.error("Failed to suppress insight:", error);
      toast.error("Failed to suppress insight");
    }
  }

  async function handleDeleteSuppression(suppressionId: string) {
    try {
      await api.deleteInsightSuppression(suppressionId);
      setSuppressions((prev) => prev.filter((s) => s.id !== suppressionId));
    } catch (error) {
      console.error("Failed to delete suppression:", error);
      toast.error("Failed to remove suppression");
    }
  }

  // Build unified feed
  const allItems = useMemo(
    () => [...buildUnifiedFeed(insights, remediationTasks, alerts), ...signingDomainItems],
    [insights, remediationTasks, alerts, signingDomainItems]
  );

  const activeItems = useMemo(
    () =>
      allItems
        .filter((i) => !i.isCompleted)
        .sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 5) - (PRIORITY_ORDER[b.priority] ?? 5)),
    [allItems]
  );

  const completedItems = useMemo(
    () =>
      allItems
        .filter((i) => i.isCompleted)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [allItems]
  );

  const counts = useMemo(() => ({
    critical: activeItems.filter((i) => i.priority === "critical").length,
    high: activeItems.filter((i) => i.priority === "high").length,
    medium: activeItems.filter((i) => i.priority === "medium").length,
    completed: completedItems.length,
  }), [activeItems, completedItems]);

  // Scroll to and highlight the deep-linked item once data loads
  useEffect(() => {
    if (!deepLinkId || deepLinkHandled.current || allItems.length === 0) return;

    // Switch to the right tab if the item is completed
    const target = allItems.find((i) => i.id === deepLinkId);
    if (target?.isCompleted && statusFilter !== "completed") {
      handleFilterChange("completed");
    }

    const el = document.getElementById(`action-item-${deepLinkId}`);
    if (el) {
      deepLinkHandled.current = true;
      setHighlightedId(deepLinkId);
      // Small delay so the DOM is settled after possible filter switch
      setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
      // Remove highlight after 2 seconds
      setTimeout(() => setHighlightedId(null), 2100);
    }
  }, [deepLinkId, allItems, statusFilter]);

  const loading = alertsLoading || insightsLoading;

  function handleFilterChange(filter: StatusFilter) {
    setStatusFilter(filter);
    const newParams = new URLSearchParams(searchParams);
    if (filter === "completed") {
      newParams.set("filter", "completed");
    } else {
      newParams.delete("filter");
    }
    setSearchParams(newParams, { replace: true });
  }

  if (loading && allItems.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Action Items</h1>
          <p className="text-slate-500">Your prioritized email security actions.</p>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Action Items</h1>
        <p className="text-slate-500">Your prioritized email security actions.</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard icon={AlertTriangle} label="Critical" value={counts.critical} color="red" />
        <SummaryCard icon={Shield} label="High" value={counts.high} color="amber" />
        <SummaryCard icon={Clock} label="Medium" value={counts.medium} color="blue" />
        <SummaryCard icon={CheckCircle2} label="Completed" value={counts.completed} color="green" />
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        <button
          onClick={() => handleFilterChange("active")}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            statusFilter === "active"
              ? "bg-slate-800 text-white"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          Active ({activeItems.length})
        </button>
        <button
          onClick={() => handleFilterChange("completed")}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            statusFilter === "completed"
              ? "bg-slate-800 text-white"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          Completed ({completedItems.length})
        </button>
      </div>

      {/* Active Items */}
      {statusFilter === "active" && (
        <div className="space-y-3">
          {activeItems.length === 0 ? (
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <CheckCircle2 className="w-16 h-16 text-emerald-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-800 mb-2">All clear</h3>
                <p className="text-slate-500">No active action items. Your email security is looking good.</p>
              </div>

              {/* Free-tier upsell: explain gated features they're missing */}
              {isFreeTier && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Lightbulb className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-slate-800 mb-1">
                        See What You Should Fix Next
                      </h3>
                      <p className="text-sm text-slate-600 mb-4">
                        Paid plans include AI-powered insights that analyze your DMARC data and
                        tell you exactly which senders to fix, plus guided remediation tasks that
                        walk you through each step.
                      </p>
                      <div className="grid sm:grid-cols-2 gap-3 mb-4">
                        <div className="flex items-start gap-2">
                          <Wrench className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-slate-700">Guided Remediation</p>
                            <p className="text-xs text-slate-500">Step-by-step tasks to fix failing senders</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-slate-700">AI Insights</p>
                            <p className="text-xs text-slate-500">Proactive analysis of your authentication gaps</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <a
                          href="/settings/billing"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg transition-colors text-sm"
                        >
                          <Crown className="w-4 h-4" />
                          Start Free Trial
                        </a>
                        <span className="text-xs text-slate-400">14-day free trial, cancel anytime</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            activeItems.map((item) => (
              <ActionItemCard
                key={item.id}
                item={item}
                id={`action-item-${item.id}`}
                highlighted={highlightedId === item.id}
                onNavigate={navigate}
                onResolveAlert={handleResolveAlert}
                onCompleteTask={handleCompleteTask}
                onSkipTask={handleSkipTask}
                onSnoozeInsight={handleSnoozeInsight}
                onResolveInsight={handleResolveInsight}
                onSuppressInsight={handleSuppressInsight}
              />
            ))
          )}

          {/* Completed section (collapsed) */}
          {completedItems.length > 0 && (
            <div className="mt-8">
              <button
                onClick={() => setShowCompleted(!showCompleted)}
                className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
              >
                {showCompleted ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                Completed ({completedItems.length})
              </button>
              {showCompleted && (
                <div className="space-y-2 mt-3">
                  {completedItems.slice(0, 20).map((item) => (
                    <div
                      key={item.id}
                      className="bg-slate-50 rounded-lg border border-slate-200 p-3 opacity-60"
                    >
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        <span className="text-sm text-slate-600 line-through">{item.title}</span>
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${SOURCE_BADGE[item.source].className}`}>
                          {SOURCE_BADGE[item.source].label}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Completed Items (full view) */}
      {statusFilter === "completed" && (
        <div className="space-y-3">
          {completedItems.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-800 mb-2">No completed items yet</h3>
              <p className="text-slate-500">Completed actions will appear here.</p>
            </div>
          ) : (
            completedItems.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-xl border border-slate-200 p-4 opacity-75"
              >
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-medium text-slate-700">{item.title}</span>
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${SOURCE_BADGE[item.source].className}`}>
                        {SOURCE_BADGE[item.source].label}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500">{item.description}</p>
                  </div>
                  <span className="text-xs text-slate-400 flex-shrink-0">
                    {new Date(item.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Permanent Suppressions */}
      {suppressions.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
            <Ban className="w-4 h-4 text-slate-400" />
            <h3 className="text-sm font-semibold text-slate-700">Not Applicable Suppressions</h3>
            <span className="ml-auto text-xs text-slate-400">
              These insight types will not resurface for the specified context. Remove to re-enable.
            </span>
          </div>
          <div className="divide-y divide-slate-100">
            {suppressions.map((s) => (
              <div key={s.id} className="flex items-center gap-3 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700">
                    {s.insightType.replace(/_/g, " ")}
                    {s.fingerprint && (
                      <span className="ml-2 text-slate-500">— {s.fingerprint}</span>
                    )}
                  </p>
                  <p className="text-xs text-slate-400">
                    {s.domainName ?? "All domains"} · Suppressed {new Date(s.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => handleDeleteSuppression(s.id)}
                  className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                  title="Remove suppression"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Summary Card
// ============================================================================

function SummaryCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  color: "red" | "amber" | "blue" | "green";
}) {
  const colorClasses = {
    red: "bg-red-50 text-red-600",
    amber: "bg-amber-50 text-amber-600",
    blue: "bg-blue-50 text-blue-500",
    green: "bg-emerald-50 text-emerald-700",
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-3">
        <div
          className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center`}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-800">{value}</p>
          <p className="text-sm text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );
}
